import { supabase } from './supabaseClient';

/**
 * Service for handling artist-related operations
 */

/**
 * Get all artists with optional filtering
 * @param {object} filters - Optional filters to apply
 * @param {number} page - Page number for pagination
 * @param {number} limit - Number of artists per page
 * @returns {Promise} - List of artists
 */
export const getArtists = async (filters = {}, page = 1, limit = 10) => {
  try {
    const offset = (page - 1) * limit;
    
    // Basic query to get all artists
    let query = supabase
      .from('profiles')
      .select('*', { count: 'exact' })
      .eq('user_type', 'artist') // Keep this filter as we only want to show artist profiles
      .order('full_name', { ascending: true });
      
    console.log('Fetching artists with filters:', filters);
    
    // Apply filters if they exist
    if (filters.instrument) {
      // Use contains for array fields - this checks if the instrument is in the array
      console.log('Filtering by instrument:', filters.instrument);
      query = query.contains('instruments', [filters.instrument]);
    }
    
    if (filters.genre) {
      // Use contains for array fields - this checks if the genre is in the array
      console.log('Filtering by genre:', filters.genre);
      query = query.contains('genres', [filters.genre]);
    }
    
    if (filters.location) {
      // Use ilike for text fields - case-insensitive partial match
      console.log('Filtering by location:', filters.location);
      query = query.ilike('location', `%${filters.location}%`);
    }
    
    if (filters.profession) {
      // Use contains for array fields - this checks if the profession is in the array
      console.log('Filtering by profession:', filters.profession);
      query = query.contains('professions', [filters.profession]);
    }
    
    // Experience filter has been removed as requested
    
    if (filters.maxRate) {
      query = query.lte('hourly_rate', filters.maxRate);
    }
    
    // Add pagination
    query = query.range(offset, offset + limit - 1);
    
    const { data, error, count } = await query;
    
    if (error) {
      throw error;
    }
    
    return { 
      artists: data || [], 
      count, 
      page, 
      limit,
      totalPages: Math.ceil(count / limit),
      error: null 
    };
  } catch (error) {
    console.error('Error fetching artists:', error.message);
    return { artists: [], count: 0, page, limit, totalPages: 0, error };
  }
};

/**
 * Get a single artist by ID
 * @param {string} artistId - Artist ID (uuid)
 * @returns {Promise} - Artist data
 */
export const getArtistById = async (artistId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('uuid', artistId)
      .eq('user_type', 'artist')
      .single();
    
    if (error) {
      throw error;
    }
    
    return { artist: data, error: null };
  } catch (error) {
    console.error('Error fetching artist:', error.message);
    return { artist: null, error };
  }
};

/**
 * Get all instruments used by artists
 * @returns {Promise} - List of unique instruments
 */
export const getAllInstruments = async () => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('instruments')
      .eq('user_type', 'artist')
      .not('instruments', 'is', null);
    
    if (error) {
      throw error;
    }
    
    // Extract all instruments and flatten the array
    const allInstruments = data
      .filter(profile => Array.isArray(profile.instruments))
      .flatMap(profile => profile.instruments);
    
    // Get unique instruments
    const uniqueInstruments = [...new Set(allInstruments)].sort();
    
    return { instruments: uniqueInstruments, error: null };
  } catch (error) {
    console.error('Error fetching instruments:', error.message);
    return { instruments: [], error };
  }
};

/**
 * Get all genres used by artists
 * @returns {Promise} - List of unique genres
 */
export const getAllGenres = async () => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('genres')
      .eq('user_type', 'artist')
      .not('genres', 'is', null);
    
    if (error) {
      throw error;
    }
    
    // Extract all genres and flatten the array
    const allGenres = data
      .filter(profile => Array.isArray(profile.genres))
      .flatMap(profile => profile.genres);
    
    // Get unique genres
    const uniqueGenres = [...new Set(allGenres)].sort();
    
    return { genres: uniqueGenres, error: null };
  } catch (error) {
    console.error('Error fetching genres:', error.message);
    return { genres: [], error };
  }
};

/**
 * Get all locations used by artists
 * @returns {Promise} - List of unique locations
 */
export const getAllLocations = async () => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('location')
      .eq('user_type', 'artist')
      .not('location', 'is', null);
    
    if (error) {
      throw error;
    }
    
    // Extract all locations
    const allLocations = data
      .filter(profile => profile.location)
      .map(profile => profile.location);
    
    // Get unique locations
    const uniqueLocations = [...new Set(allLocations)].sort();
    
    return { locations: uniqueLocations, error: null };
  } catch (error) {
    console.error('Error fetching locations:', error.message);
    return { locations: [], error };
  }
};
